export interface PasskeyAuthenticationOptions {
  challenge_token: string;
  public_key: {
    challenge: string;
    rp_id: string;
    timeout_ms: number;
    user_verification: "required";
    allow_credentials: Array<{ id: string; transports: string[] }>;
  };
}

export interface PasskeyEnrollmentOptions {
  challenge_token: string;
  public_key: {
    challenge: string;
    rp: { id: string; name: string };
    user: { id: string; name: string; display_name: string };
    pub_key_cred_params: Array<{ type: "public-key"; alg: number }>;
    timeout_ms: number;
    attestation: "none";
    authenticator_selection: { resident_key: "preferred"; user_verification: "required" };
    exclude_credentials: Array<{ id: string; transports: string[] }>;
  };
}

function decodeBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("The passkey service returned malformed binary data.");
  const binary = atob(value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "="));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function encodeBase64Url(value: ArrayBuffer): string {
  let binary = "";
  for (const byte of new Uint8Array(value)) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function descriptor(value: { id: string; transports: string[] }): PublicKeyCredentialDescriptor {
  const supported = value.transports.filter((transport) => ["ble", "hybrid", "internal", "nfc", "usb"].includes(transport));
  return {
    type: "public-key",
    id: decodeBase64Url(value.id).buffer as ArrayBuffer,
    ...(supported.length > 0 ? { transports: supported as AuthenticatorTransport[] } : {}),
  };
}

export function passkeysSupported(): boolean {
  return typeof window.PublicKeyCredential === "function" && Boolean(navigator.credentials);
}

export async function getPasskeyAssertion(options: PasskeyAuthenticationOptions): Promise<Record<string, string>> {
  if (!passkeysSupported()) throw new Error("This browser does not support passkeys.");
  const credential = await navigator.credentials.get({
    publicKey: {
      challenge: decodeBase64Url(options.public_key.challenge).buffer as ArrayBuffer,
      rpId: options.public_key.rp_id,
      timeout: options.public_key.timeout_ms,
      userVerification: options.public_key.user_verification,
      allowCredentials: options.public_key.allow_credentials.map(descriptor),
    },
  });
  if (!(credential instanceof PublicKeyCredential) || !(credential.response instanceof AuthenticatorAssertionResponse)) {
    throw new Error("The browser returned an invalid passkey assertion.");
  }
  return {
    credential_id: encodeBase64Url(credential.rawId),
    client_data_json: encodeBase64Url(credential.response.clientDataJSON),
    authenticator_data: encodeBase64Url(credential.response.authenticatorData),
    signature: encodeBase64Url(credential.response.signature),
  };
}

export async function createPasskeyCredential(options: PasskeyEnrollmentOptions): Promise<Record<string, unknown>> {
  if (!passkeysSupported()) throw new Error("This browser does not support passkeys.");
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: decodeBase64Url(options.public_key.challenge).buffer as ArrayBuffer,
      rp: options.public_key.rp,
      user: {
        id: decodeBase64Url(options.public_key.user.id).buffer as ArrayBuffer,
        name: options.public_key.user.name,
        displayName: options.public_key.user.display_name,
      },
      pubKeyCredParams: options.public_key.pub_key_cred_params,
      timeout: options.public_key.timeout_ms,
      attestation: options.public_key.attestation,
      authenticatorSelection: {
        residentKey: options.public_key.authenticator_selection.resident_key,
        userVerification: options.public_key.authenticator_selection.user_verification,
      },
      excludeCredentials: options.public_key.exclude_credentials.map(descriptor),
    },
  });
  if (!(credential instanceof PublicKeyCredential) || !(credential.response instanceof AuthenticatorAttestationResponse)) {
    throw new Error("The browser returned an invalid passkey credential.");
  }
  const response = credential.response as AuthenticatorAttestationResponse & { getAuthenticatorData?: () => ArrayBuffer };
  const authenticatorData = response.getAuthenticatorData?.();
  if (!authenticatorData) throw new Error("This browser cannot expose verified passkey credential data.");
  return {
    credential_id: encodeBase64Url(credential.rawId),
    client_data_json: encodeBase64Url(response.clientDataJSON),
    authenticator_data: encodeBase64Url(authenticatorData),
    transports: response.getTransports?.() ?? [],
  };
}
